import { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useLocation } from "react-router-dom"; // Added useLocation
import Preloader from "./Preloader"; // Adjust path
import { AuthContext } from "../context/AuthContext"; // Adjust path
import styles from '../assets/css/Login.module.css'; // Import NEW CSS Module
import { FaWhatsapp, FaTelegramPlane } from "react-icons/fa"; // Icons

// Custom hook to get query params
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function Login() {
  const [preloader, setPreloader] = useState(false);
  const [username, setUsername] = useState(""); // Mobile Number
  const [password, setPassword] = useState(""); // OTP
  const [refferal_code, setRefferalCode] = useState("");
  const [ButtonName, setButtonName] = useState("Send OTP");
  const [OtpContainer, setOtpContainer] = useState(false); // Show OTP field flag
  const [RefferalContainer, setRefferalContainer] = useState(false); // Show Referral field flag
  const query = useQuery();
  const agentId = query.get('agent'); // Get agent ID from URL query param

  const {
      isAuthenticated,
      login,
      WhatsappMessageSupport,
      TelegramLink = "https://t.me/aghoribabamatka", // Default Telegram link
      app_name = "AGHORI MATKA", // Default App Name
      app_logo = "/favicon.png" // Default Logo
    } = useContext(AuthContext);

  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Toast message function
  const message = (status, text) => {
    const options = {
        position: "top-right", autoClose: 3000, hideProgressBar: false,
        closeOnClick: true, pauseOnHover: true, draggable: true,
        progress: undefined, theme: "dark", // Use dark theme matching Aghori
    };
    if (status === 1) toast.success(text, options);
    else if (status === 0) toast.error(text, options);
    else if (status === 2) toast.warning(text, options);
  };

  // Handle Login/OTP Request
  const handleLogin = () => {
    // Basic mobile number validation
    if (username === "" || !/^\d{10}$/.test(username)) {
      message(0, "Please enter a valid 10-digit Mobile Number.");
      return;
    }

    setPreloader(true); // Show preloader during API call

    if (!OtpContainer) {
      // --- Request OTP ---
      let data = { mobile: username };
      if (login) {
          login(data, (response) => {
              setPreloader(false);
              if (response.status === 0) {
                  message(0, response.msg || "Failed to send OTP.");
              } else {
                  setOtpContainer(true); // Show OTP field
                  setButtonName("Verify OTP & Login");
                  message(1, response.msg || "OTP sent successfully.");
                  // Show referral code input if API indicates new user
                  if (response.purpose === 1) { // Check purpose flag from API
                      setRefferalContainer(true);
                  } else {
                      setRefferalContainer(false); // Ensure it's hidden otherwise
                  }
              }
          });
      } else {
           setPreloader(false);
           message(0, "Login function not available.");
      }
    } else {
      // --- Verify OTP & Login ---
      if (password === "" || !/^\d{4,6}$/.test(password)) { // Basic OTP validation (4-6 digits)
        setPreloader(false);
        message(0, "Please enter a valid OTP.");
        return;
      }
      // Include referral code only if the container is shown
      let data = {
        mobile: username,
        otp: password,
        ...(RefferalContainer && refferal_code && { refferal: refferal_code }), // Conditionally add referral if not empty
        ...(agentId && { agentId: agentId }) // Conditionally add agentId
      };

      if (login) {
          login(data, (response) => {
              setPreloader(false);
              if (response.status === 0) {
                  message(0, response.msg || "Login failed.");
              } else {
                  message(1, response.msg || "Login successful!");
                  // Auth context should handle redirect via useEffect on isAuthenticated change
              }
          });
      } else {
           setPreloader(false);
           message(0, "Login function not available.");
      }
    }
  };


  // Framer Motion variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
  };
  const itemVariants = {
    hidden: { y: 15, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  return (
    <>
      <Preloader status={preloader} />
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      {/* Use the Aghori theme container */}
      <div className={styles.aghoriLoginContainer}>
        <motion.div
            className={styles.aghoriLoginBox}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0 }}
        >
            {/* --- Logo and Title --- */}
            <motion.div className={styles.aghoriLogoContainer} variants={itemVariants}>
              <img src={app_logo} className={styles.aghoriLogo} alt="Logo" />
              <p className={styles.aghoriTitle}>{app_name}</p>
            </motion.div>

            {/* --- Input Fields --- */}
            <motion.div className={styles.aghoriInputGroup} variants={itemVariants}>
                <label htmlFor="mobileInput" className={styles.aghoriInputLabel}>Mobile Number</label>
                <input
                    id="mobileInput"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    className={styles.aghoriFormInput}
                    value={username}
                    onInput={(e) => setUsername(e.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="Enter 10 Digit Number"
                    disabled={OtpContainer} // Disable after OTP sent
                />
            </motion.div>

            {/* Referral Code Input (Conditional) */}
            {RefferalContainer && (
                <motion.div className={styles.aghoriInputGroup} variants={itemVariants}>
                    <label htmlFor="referralInput" className={styles.aghoriInputLabel}>Referral Code (Optional)</label>
                    <input
                        id="referralInput"
                        type="text" // Allow text/numbers for referral
                        className={styles.aghoriFormInput}
                        value={refferal_code}
                        onInput={(e) => setRefferalCode(e.target.value)}
                        placeholder="Enter Referral Code"
                    />
                </motion.div>
            )}

            {/* OTP Input (Conditional) */}
            {OtpContainer && (
                <motion.div className={styles.aghoriInputGroup} variants={itemVariants}>
                    <label htmlFor="otpInput" className={styles.aghoriInputLabel}>Enter OTP</label>
                    <input
                        id="otpInput"
                        type="tel" // Use tel for number pads
                        inputMode="numeric"
                        maxLength={6} // Assuming OTP length
                        className={styles.aghoriFormInput}
                        value={password}
                        onInput={(e) => setPassword(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="Enter OTP Received"
                    />
                </motion.div>
            )}

            {/* --- Login/OTP Button --- */}
            <motion.div className={styles.aghoriButtonContainer} variants={itemVariants}>
                <button className={styles.aghoriLoginButton} onClick={handleLogin} disabled={preloader}>
                    {preloader ? 'Processing...' : ButtonName}
                </button>
            </motion.div>

            {/* --- Help Buttons --- */}
            <motion.div className={styles.aghoriHelpButtonContainer} variants={itemVariants}>
                <button
                    className={`${styles.aghoriHelpButton} ${styles.aghoriWhatsappHelpButton}`}
                    onClick={() => { if (WhatsappMessageSupport) window.open(WhatsappMessageSupport, '_blank'); }}
                    disabled={!WhatsappMessageSupport}
                >
                    <FaWhatsapp /> WhatsApp Help
                </button>
                <button
                    className={`${styles.aghoriHelpButton} ${styles.aghoriTelegramHelpButton}`}
                    onClick={() => { if (TelegramLink) window.open(TelegramLink, '_blank'); }}
                    disabled={!TelegramLink}
                >
                    <FaTelegramPlane /> Telegram Help
                </button>
            </motion.div>

        </motion.div>
      </div>
    </>
  );
}

export default Login;
